import React, { useState, useEffect, useRef, useMemo } from "react";
import { NavLink, useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { Bell } from "lucide-react";
import { useAuth, useLang, http } from "@/lib/api";
import Logo from "@/components/Logo";
import "@/components/Navbar.css";

// Repli affiché si le backend ne renvoie pas de bandeau SABAT (ex : hors ligne),
// pour que la bande crème reste toujours présente en haut de la page Articles.
const SABAT_DEFAUT = {
  actif: true,
  intervalle_ms: 3000,
  items: [
    { id: "sabat-2026", icone: "🏆", texte: "SABAT 2026 - 4ème Édition - Octobre au Togo - Réservez vos places", url: "https://afrifute.com" },
  ],
};

const CAT_PILLS = [
  { name: "Décryptages",      slug: "tendance" },            // WP slug réel
  { name: "Opportunités",     slug: "opportunites" },
  { name: "Événements",       slug: "evenements" },          // WP cat nommée "Rencontres"
  { name: "Diaspora",         slug: "diaspora" },
  { name: "Guides pratiques", slug: "ressources-productivite" },
  { name: "Outils",           slug: "outils" },
];

export default function Navbar() {
  const { user } = useAuth();
  const { lang, t, toggle } = useLang();
  const nav = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const [filterOpen, setFilterOpen] = useState(false);     // desktop (nav-actions)
  const [filterOpenSm, setFilterOpenSm] = useState(false); // mobile/tablette (centre)
  const filterRef = useRef(null);
  const filterRefSm = useRef(null);
  const [unread, setUnread] = useState(0);
  const [notifsOpen, setNotifsOpen] = useState(false);
  const [notifs, setNotifs] = useState([]);
  const notifRef = useRef(null);
  const [sabat, setSabat] = useState({ actif: false, intervalle_ms: 3000, items: [] });
  const [sabatIdx, setSabatIdx] = useState(0);

  const isAccueil = location.pathname === "/";
  const isArticleShow = location.pathname.startsWith("/article/");   // page d'un article
  const isArticles = location.pathname === "/articles" || isArticleShow; // navbar éditoriale (index + show)
  const isAdmin = location.pathname.startsWith("/admin");
  const selectedCat = searchParams.get("cat") || "";
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQ, setSearchQ] = useState(searchParams.get("q") || "");
  const searchInputRef = useRef(null);

  const selectedCatName = CAT_PILLS.find(c => c.slug === selectedCat)?.name || null;

  const selectCat = (slug) => {
    // Depuis la page d'un article, filtrer par catégorie renvoie vers l'index.
    if (isArticleShow) nav(slug ? `/articles?cat=${slug}` : "/articles");
    else setSearchParams(selectedCat === slug ? {} : { cat: slug });
    setOpen(false);
    setFilterOpen(false);
    setFilterOpenSm(false);
  };

  const openSearch = () => {
    setSearchOpen(true);
    setTimeout(() => searchInputRef.current?.focus(), 50);
  };

  const closeSearch = () => {
    setSearchOpen(false);
    setSearchQ("");
    setSearchParams(p => { const n = new URLSearchParams(p); n.delete("q"); return n; });
  };

  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearchQ(v);
    // Depuis la page d'un article, la recherche s'effectue sur l'index des articles.
    if (isArticleShow) { nav(v ? `/articles?q=${encodeURIComponent(v)}` : "/articles"); return; }
    setSearchParams(p => {
      const n = new URLSearchParams(p);
      v ? n.set("q", v) : n.delete("q");
      return n;
    });
  };

  useEffect(() => {
    if (!isArticles) { setSearchOpen(false); setSearchQ(""); }
  }, [isArticles]);

  const dateAccueil = useMemo(() => {
    const jours = ['Dimanche','Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi'];
    const mois = ['janvier','février','mars','avril','mai','juin','juillet','août','septembre','octobre','novembre','décembre'];
    const d = new Date();
    return `${jours[d.getDay()]} ${d.getDate()} ${mois[d.getMonth()]} ${d.getFullYear()}`;
  }, []);

  // Repli affiché si aucune solution / idée n'est mise en actualité depuis l'admin.
  const EVENTS_TICKER = useMemo(() => [
    `📰 Nouvel article : <b>Ara Market, le pari du reconditionné</b>`,
    `✅ Solution vérifiée : <b>TillMoWoo</b>`,
    `💡 Idée proposée : <b>Un carnet de santé numérique partagé</b>`,
    `🟢 Nouvelle solution ajoutée : <b>FarmUp</b>`,
    `🎉 Idée adoptée : <b>Covoiturage inter-villes → TransAfrik</b>`,
    `📰 Nouvel article : <b>Salon des Entrepreneurs Congolais SECKIN 2026</b>`,
    `✅ Solution vérifiée : <b>Djeli</b>`,
  ], []);

  // File d'actualité gérée depuis l'admin (solutions + idées cochées « en actualité »).
  const [actuItems, setActuItems] = useState([]);
  useEffect(() => {
    http.get("/actualite/public")
      .then(r => setActuItems(r.data?.items || []))
      .catch(() => setActuItems([]));
  }, []);

  const tickerItems = useMemo(() => {
    const esc = (s) => String(s || "").replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));
    const built = actuItems.map(it =>
      it.type === "solution"
        ? `✅ Solution : <b>${esc(it.label)}</b>`
        : `💡 Idée : <b>${esc(it.label)}</b>`
    );
    return built.length ? built : EVENTS_TICKER;   // repli si rien en actualité
  }, [actuItems, EVENTS_TICKER]);

  // Bandeau SABAT : messages gérés depuis l'admin, défilant l'un après l'autre.
  // Si le backend est injoignable, on retombe sur un message par défaut pour ne
  // jamais laisser la bande vide.
  useEffect(() => {
    http.get("/sabat/public")
      .then(r => { setSabat(r.data); setSabatIdx(0); })
      .catch(() => { setSabat(SABAT_DEFAUT); setSabatIdx(0); });
  }, []);

  const sabatItems = sabat.actif ? (sabat.items || []) : [];

  useEffect(() => {
    if (sabatItems.length <= 1) return;
    const delai = Math.max(1000, sabat.intervalle_ms || 3000);
    const timer = setInterval(() => setSabatIdx(i => (i + 1) % sabatItems.length), delai);
    return () => clearInterval(timer);
  }, [sabatItems.length, sabat.intervalle_ms]);

  const sabatCourant = sabatItems.length ? sabatItems[sabatIdx % sabatItems.length] : null;

  // Expose la hauteur du bandeau SABAT (36px s'il est visible, 0 sinon) : la
  // barre de date collante se recale automatiquement quand il est masqué.
  useEffect(() => {
    document.documentElement.style.setProperty("--sabat-h", sabatItems.length ? "36px" : "0px");
  }, [sabatItems.length]);

  useEffect(() => {
    if (!user) { setUnread(0); return; }
    const fetch = () => http.get("/notifications/unread-count").then(r => setUnread(r.data.count)).catch(() => {});
    fetch();
    const timer = setInterval(fetch, 60000); // refresh toutes les minutes
    return () => clearInterval(timer);
  }, [user]);

  // Menu déroulant des notifications (backend déjà en place).
  const rafraichirNonLus = () => http.get("/notifications/unread-count").then(r => setUnread(r.data.count)).catch(() => {});
  const ouvrirNotifs = () => {
    if (notifsOpen) { setNotifsOpen(false); return; }
    http.get("/notifications").then(r => setNotifs(r.data || [])).catch(() => setNotifs([]));
    setNotifsOpen(true);
  };
  const cliquerNotif = async (n) => {
    if (!n.read) { try { await http.patch(`/notifications/${n.id}/read`); } catch { /* ignore */ } rafraichirNonLus(); }
    setNotifsOpen(false);
    if (n.lien) { if (/^https?:\/\//.test(n.lien)) window.open(n.lien, "_blank"); else nav(n.lien); }
  };
  const toutMarquerLu = async () => {
    try { await http.post("/notifications/read"); } catch { /* ignore */ }
    setNotifs(ns => ns.map(n => ({ ...n, read: true }))); setUnread(0);
  };
  useEffect(() => {
    if (!notifsOpen) return;
    const onDoc = (e) => { if (notifRef.current && !notifRef.current.contains(e.target)) setNotifsOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [notifsOpen]);

  const proposerSolution = () => {
    setOpen(false);
    if (!user) { nav("/connexion?next=proposer"); return; }
    if (user.role === "entreprise") { nav("/espace-entreprise?new=1"); return; }
    nav("/admin");
  };

  useEffect(() => {
    const handler = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
      if (filterRef.current && !filterRef.current.contains(e.target)) setFilterOpen(false);
      if (filterRefSm.current && !filterRefSm.current.contains(e.target)) setFilterOpenSm(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Mesure la hauteur réelle du nav et l'expose en --nav-h : les barres
  // collantes (date/tagline, catégories) restent calées quel que soit l'écran.
  useEffect(() => {
    const nav = document.querySelector(".site-nav");
    if (!nav) return;
    const apply = () => document.documentElement.style.setProperty("--nav-h", `${Math.round(nav.getBoundingClientRect().height)}px`);
    apply();
    const ro = new ResizeObserver(apply);
    ro.observe(nav);
    window.addEventListener("resize", apply);
    return () => { ro.disconnect(); window.removeEventListener("resize", apply); };
  }, [location.pathname]);

  const close = () => setOpen(false);

  if (isArticles) return (
    <>
      {searchOpen && <div className="nav-search-overlay" onClick={closeSearch} />}

      <nav className="site-nav" data-testid="site-nav">
        <NavLink to="/" className="brand" data-testid="brand-home">
          <Logo height={34} label="Éditorial" />
        </NavLink>

        {/* Centre : Articles · Solutions · Favoris (desktop) / Solutions + CATÉGORIE (≤1024px) */}
        <div className="nav-filter-wrap">
          <NavLink to="/articles" className={({ isActive }) => `ac-nav-link nav-filter-lg-only ${isActive ? "active" : ""}`} data-testid="nav-editorial">Éditorial</NavLink>
          <NavLink to="/solutions" className={({ isActive }) => `ac-nav-link ${isActive ? "active" : ""}`} data-testid="nav-catalogue">Atlas</NavLink>
          <div className="nav-filter-inner nav-filter-sm-only" ref={filterRefSm}>
            <button className={`nav-filter-btn ${selectedCat ? "on" : ""} ${filterOpenSm ? "open" : ""}`} onClick={() => setFilterOpenSm(o => !o)}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
              {selectedCatName || "CATÉGORIES"}
              <svg className={`nav-filter-arrow ${filterOpenSm ? "open" : ""}`} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            {filterOpenSm && (
              <div className="nav-filter-dropdown">
                <button className={`nav-filter-opt ${!selectedCat ? "on" : ""}`} onClick={() => { setSearchParams({}); setFilterOpenSm(false); }}>Tous les articles</button>
                <div className="ndd-sep" />
                {CAT_PILLS.map(c => (
                  <button key={c.slug} className={`nav-filter-opt ${selectedCat === c.slug ? "on" : ""}`} onClick={() => selectCat(c.slug)}>{c.name}</button>
                ))}
              </div>
            )}
          </div>

          {/* Tablette / mobile (≤1024px) : la barre de recherche remplace le bouton CATÉGORIES de la navbar (les catégories restent dans le menu ☰). */}
          <div className={`nav-search-bar nav-search-sm ${searchOpen ? "active" : ""}`}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="nav-search-bar-icon"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input
              className="nav-search-bar-input"
              placeholder="Rechercher…"
              value={searchQ}
              onFocus={openSearch}
              onChange={handleSearchChange}
              onKeyDown={e => e.key === "Escape" && closeSearch()}
            />
            {searchOpen && <button className="nav-search-close" onClick={closeSearch} aria-label="Fermer">✕</button>}
          </div>
        </div>

        {/* Actions droite : recherche + CATÉGORIE remplace Éditorial (desktop > 1024px) */}
        <div className="nav-actions nav-actions-articles">
          <div className={`nav-search-bar ${searchOpen ? "active" : ""}`}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" className="nav-search-bar-icon"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/></svg>
            <input
              ref={searchInputRef}
              className="nav-search-bar-input"
              placeholder="Rechercher…"
              value={searchQ}
              onFocus={openSearch}
              onChange={handleSearchChange}
              onKeyDown={e => e.key === "Escape" && closeSearch()}
            />
            {searchOpen && <button className="nav-search-close" onClick={closeSearch} aria-label="Fermer">✕</button>}
          </div>
          <div className="nav-filter-inner" ref={filterRef}>
            <button className={`nav-filter-btn ${selectedCat ? "on" : ""} ${filterOpen ? "open" : ""}`} onClick={() => setFilterOpen(o => !o)}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
              {selectedCatName || "CATÉGORIES"}
              <svg className={`nav-filter-arrow ${filterOpen ? "open" : ""}`} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>
            </button>
            {filterOpen && (
              <div className="nav-filter-dropdown nav-filter-dropdown-right">
                <button className={`nav-filter-opt ${!selectedCat ? "on" : ""}`} onClick={() => { setSearchParams({}); setFilterOpen(false); }}>Tous les articles</button>
                <div className="ndd-sep" />
                {CAT_PILLS.map(c => (
                  <button key={c.slug} className={`nav-filter-opt ${selectedCat === c.slug ? "on" : ""}`} onClick={() => selectCat(c.slug)}>{c.name}</button>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="nav-mobile" ref={menuRef}>
          <button className={`nav-burger ${open ? "open" : ""}`} onClick={() => setOpen(o => !o)} aria-label="Menu" aria-expanded={open}>
            <span /><span /><span />
          </button>
          {open && (
            <div className="nav-dropdown">
              <NavLink to="/articles" className={({ isActive }) => `ndd-link ${isActive ? "on" : ""}`} onClick={close}>Articles</NavLink>
              <NavLink to="/solutions" className={({ isActive }) => `ndd-link ${isActive ? "on" : ""}`} onClick={close}>Solutions</NavLink>
              <div className="ndd-sep ndd-sep-gold" />
              <button className={`ndd-link cta ${selectedCat ? "ndd-cat-on" : ""}`} onClick={() => setFilterOpenSm(o => !o)}>
                <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="4" y1="6" x2="20" y2="6"/><line x1="8" y1="12" x2="16" y2="12"/><line x1="11" y1="18" x2="13" y2="18"/></svg>
                {selectedCatName || "CATÉGORIES"}
                <svg className={`nav-filter-arrow ${filterOpenSm ? "open" : ""}`} width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="6 9 12 15 18 9"/></svg>
              </button>
              {filterOpenSm && (
                <>
                  <button className={`ndd-link ${!selectedCat ? "on" : ""}`} onClick={() => { setSearchParams({}); setFilterOpenSm(false); close(); }}>Tous les articles</button>
                  {CAT_PILLS.map(c => (
                    <button key={c.slug} className={`ndd-link ${selectedCat === c.slug ? "on" : ""}`} onClick={() => { selectCat(c.slug); }}>{c.name}</button>
                  ))}
                </>
              )}
            </div>
          )}
        </div>
      </nav>

      {/* 2. Ticker ACTUALITÉS */}
      <div className="ac-pouls">
        <div className="ac-piste">
          <div className="ac-piste-inner">
            {[...tickerItems, ...tickerItems].map((e, i) => (
              <span key={i} dangerouslySetInnerHTML={{ __html: e }} />
            ))}
          </div>
        </div>
      </div>

      {/* 3. SABAT - fond crème, bordure dorée en bas. Messages défilants gérés
          depuis l'admin (Réglages › Bandeau SABAT), un toutes les N secondes. */}
      {sabatCourant && (
        <div className="ed-top-sabat">
          {sabatItems.map((it, i) => {
            const contenu = <>{it.icone && <span className="ed-sabat-ico">{it.icone}</span>}{it.texte}</>;
            return (
              <span key={it.id || i} className={`ed-sabat-msg ${i === (sabatIdx % sabatItems.length) ? "on" : ""}`}>
                {it.url ? <a href={it.url} target="_blank" rel="noreferrer">{contenu}</a> : contenu}
              </span>
            );
          })}
        </div>
      )}

      {/* Barre de date retirée sur la page Articles : ce sont désormais les
          chips de catégories (dans Articles.js) qui font office de barre
          collante sous le SABAT, pour un rendu plus « appli ». */}
    </>
  );

  if (isAccueil || isAdmin) return (
    <>
      <nav className="site-nav nav-3zones" data-testid="site-nav">
        {/* Gauche : logo */}
        <NavLink to="/" className="brand" data-testid="brand-home">
          <Logo height={38} showAtlas={false} />
        </NavLink>
        {/* Centre : les 3 liens, bien centrés */}
        <div className="ac-nav-links">
          <NavLink to="/" end className={({ isActive }) => `ac-nav-link ${isActive ? "active" : ""}`}>Accueil</NavLink>
          <NavLink to="/articles" className={({ isActive }) => `ac-nav-link ${isActive ? "active" : ""}`}>Éditorial</NavLink>
          <NavLink to="/solutions" className={({ isActive }) => `ac-nav-link ${isActive ? "active" : ""}`}>Atlas</NavLink>
          <NavLink to="/services" className={({ isActive }) => `ac-nav-link ${isActive ? "active" : ""}`}>Services</NavLink>
        </div>

        {/* Droite : NEWSLETTER + langue + burger */}
        <div className="ac-nav-right">
          <NavLink to="/newsletter" className="ac-btn-newsletter nav-hide-md">NEWSLETTER</NavLink>
          <div className="nav-mobile" ref={menuRef}>
          <button className="lang-toggle" onClick={toggle} data-testid="lang-toggle" title="Français / English" aria-label={`Langue : ${lang.toUpperCase()}, changer`}>{lang.toUpperCase()}</button>
          <button className={`nav-burger ${open ? "open" : ""}`} onClick={() => setOpen(o => !o)} aria-label="Menu" aria-expanded={open}>
            <span /><span /><span />
          </button>
          {open && (
            <div className="nav-dropdown">
              <NavLink to="/" end className={({ isActive }) => `ndd-link ${isActive ? "on" : ""}`} onClick={close}>Accueil</NavLink>
              <NavLink to="/articles" className={({ isActive }) => `ndd-link ${isActive ? "on" : ""}`} onClick={close}>Éditorial</NavLink>
              <NavLink to="/solutions" className={({ isActive }) => `ndd-link ${isActive ? "on" : ""}`} onClick={close}>Atlas</NavLink>
              <NavLink to="/services" className={({ isActive }) => `ndd-link ${isActive ? "on" : ""}`} onClick={close}>Services</NavLink>
              <div className="ndd-sep ndd-sep-gold" />
              <NavLink to="/newsletter" className="ndd-link cta ndd-mobile-only" onClick={close}>Newsletter</NavLink>
            </div>
          )}
          </div>
        </div>
      </nav>
      {/* Sous-barre : uniquement sur l'accueil (élément figé), pas dans l'admin */}
      {isAccueil && (
        <div className="ac-sub-barre">
          <span className="ac-sub-date">{dateAccueil}</span>
          <span className="ac-sub-tagline">L'intelligence africaine en mouvement</span>
          <span className="ac-sub-region">Afrique · Diaspora</span>
        </div>
      )}
    </>
  );

  return (
    <>
    <nav className="site-nav nav-3zones" data-testid="site-nav">
      <NavLink to="/" className="brand" data-testid="brand-home">
        <Logo height={34} label="Atlas" />
      </NavLink>

      {/* Liens de navigation - style accueil */}
      <div className="nav-links">
        <NavLink to="/solutions" className={({ isActive }) => `ac-nav-link ${isActive ? "active" : ""}`} data-testid="nav-catalogue">{t("solutions")}</NavLink>
        <NavLink to="/articles" className={({ isActive }) => `ac-nav-link nav-hide-md ${isActive ? "active" : ""}`} data-testid="nav-editorial">Articles</NavLink>
        <NavLink to="/services" className={({ isActive }) => `ac-nav-link nav-hide-md ${isActive ? "active" : ""}`} data-testid="nav-services">Services</NavLink>
        {/* « Mon espace » est désormais un bouton flottant (EntrepriseFlottant), présent sur toutes les pages. */}
      </div>

      {/* Droite : actions + langue + burger, regroupés dans une zone alignée à droite */}
      <div className="ac-nav-right">
      <div className="nav-actions">
        {user && (
          <div ref={notifRef} style={{ position: "relative" }}>
            <button style={{ position: "relative", background: "none", border: "none", cursor: "pointer", padding: "4px 6px", color: "rgba(255,255,255,.65)" }}
              onClick={ouvrirNotifs} title="Notifications" data-testid="nav-cloche"
              aria-label={unread > 0 ? `Notifications, ${unread} non lues` : "Notifications"}
              aria-haspopup="true" aria-expanded={notifsOpen}>
              <Bell size={18} aria-hidden="true" />
              {unread > 0 && <span style={{ position: "absolute", top: 0, right: 0, background: "#C0392B", color: "#fff", borderRadius: "50%", fontSize: 9, fontWeight: 700, width: 15, height: 15, display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "'IBM Plex Mono',monospace" }}>{unread > 9 ? "9+" : unread}</span>}
            </button>
            {notifsOpen && (
              <div className="notif-menu" data-testid="notif-menu">
                <div className="notif-head">
                  <span>Notifications</span>
                  {notifs.some(n => !n.read) && <button className="notif-tout-lu" onClick={toutMarquerLu}>Tout marquer lu</button>}
                </div>
                <div className="notif-liste">
                  {notifs.length === 0
                    ? <div className="notif-vide">Aucune notification.</div>
                    : notifs.map((n) => (
                      <button key={n.id} className={`notif-item${n.read ? "" : " non-lu"}`} onClick={() => cliquerNotif(n)}>
                        <span className="notif-ic" style={{ color: n.color || "var(--slate-soft)" }}>{n.icon || "•"}</span>
                        <span className="notif-txt">
                          <span className="notif-msg">{n.message}</span>
                          <span className="notif-date">{n.created_at ? new Date(n.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : ""}</span>
                        </span>
                        {!n.read && <span className="notif-dot" />}
                      </button>
                    ))}
                </div>
              </div>
            )}
          </div>
        )}
        {/* Déconnexion retirée : elle est désormais dans le dashboard (BC / Admin). */}
        {!user && (
          <NavLink to="/connexion" className="ac-btn-newsletter ac-btn-ghost" data-testid="nav-login">{t("connexion")}</NavLink>
        )}
        <button className="ac-btn-newsletter" onClick={proposerSolution} data-testid="nav-proposer">{t("nav_proposer")}</button>
      </div>

      {/* Groupe droite toujours visible : langue + burger */}
      <div className="nav-mobile" ref={menuRef}>
        {/* Sélecteur de langue : TOUJOURS visible (desktop + mobile), coin haut-droit. */}
        <button className="lang-toggle" onClick={toggle} data-testid="lang-toggle" title="Français / English" aria-label={`Langue : ${lang.toUpperCase()}, changer`}>{lang.toUpperCase()}</button>
        <button
          className={`nav-burger ${open ? "open" : ""}`}
          onClick={() => setOpen(o => !o)}
          aria-label="Menu"
          aria-expanded={open}
          data-testid="nav-burger"
        >
          <span /><span /><span />
        </button>
        {open && (
          <div className="nav-dropdown" data-testid="nav-dropdown">
            <NavLink to="/solutions" className={({ isActive }) => `ndd-link ndd-mobile-only ${isActive ? "on" : ""}`} onClick={close}>{t("solutions")}</NavLink>
            <NavLink to="/articles" className={({ isActive }) => `ndd-link ${isActive ? "on" : ""}`} onClick={close}>Articles</NavLink>
            <NavLink to="/services" className={({ isActive }) => `ndd-link ${isActive ? "on" : ""}`} onClick={close}>Services</NavLink>
            {/* « Mon espace » : bouton flottant (EntrepriseFlottant) sur toutes les pages. */}
            {/* Langue : bouton FR/EN dédié dans la navbar. Déconnexion : dans le dashboard. */}
            {!user && (
              <>
                <div className="ndd-sep" />
                <NavLink to="/connexion" className="ndd-link cta" onClick={close}>{t("connexion")}</NavLink>
              </>
            )}
            <div className="ndd-sep ndd-sep-gold" />
            <button className="ndd-link cta-gold" onClick={proposerSolution}>{t("nav_proposer")}</button>
          </div>
        )}
      </div>
      </div>
    </nav>

    </>

  );
}
